import { useState, useEffect } from 'react';
import { ShieldAlert, X } from 'lucide-react';

/**
 * Soft amber-tone warning shown when the user's sign-in originated from a
 * different network / browser / device than their previous session.
 *
 * Dismissal is intentionally **client-side only**: we store an ack key in
 * `sessionStorage` keyed by the login timestamp, so:
 *   - the user can banish the chip for the rest of this browser session,
 *   - but the *next* anomalous sign-in (new timestamp) automatically re-arms it,
 *   - and a server-side login from a consistent network auto-clears the
 *     server-stored `signinAnomaly` flag anyway.
 *
 * Renders nothing when `anomaly` is falsy (the common case) or already acked.
 */
export function SigninAnomalyChip({
  anomaly,
  lastLoginAt,
  variant = 'panel',   // 'panel' = full row (settings) | 'chip' = compact (daybreak)
  testId = 'signin-anomaly-chip',
}) {
  const ackKey = lastLoginAt ? `polkdesq-anomaly-ack-${lastLoginAt}` : null;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!ackKey) return;
    try {
      if (sessionStorage.getItem(ackKey) === '1') setDismissed(true);
      else setDismissed(false);
    } catch {
      // sessionStorage can throw in strict private-mode browsers — degrade silently.
    }
  }, [ackKey]);

  if (!anomaly || dismissed) return null;

  const handleDismiss = () => {
    if (ackKey) {
      try { sessionStorage.setItem(ackKey, '1'); } catch { /* ignore */ }
    }
    setDismissed(true);
  };

  const copy = anomaly.summary || 'from a new device';
  const previousLabel = anomaly.previousLabel || null;

  if (variant === 'chip') {
    return (
      <div
        data-testid={testId}
        className="flex items-center gap-2 mt-2 px-3 py-1.5 rounded-full text-[11px]"
        style={{
          background: 'var(--pd-warning-soft, rgba(217, 119, 6, 0.10))',
          color: 'var(--pd-warning)',
          border: '1px solid var(--pd-warning-soft, rgba(217, 119, 6, 0.30))',
        }}
      >
        <ShieldAlert className="w-3 h-3" />
        <span>
          Signed in {copy} — was that you?
        </span>
        <button
          data-testid={`${testId}-dismiss`}
          onClick={handleDismiss}
          className="ml-1 underline underline-offset-2 hover:no-underline"
          style={{ color: 'var(--pd-warning)' }}
        >
          Yes
        </button>
      </div>
    );
  }

  // 'panel' variant — fuller treatment for Settings.
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2 mt-3 p-3 rounded-xl text-[11px]"
      style={{
        background: 'var(--pd-warning-soft, rgba(217, 119, 6, 0.08))',
        border: '1px solid var(--pd-warning-soft, rgba(217, 119, 6, 0.28))',
        color: 'var(--pd-text)',
      }}
    >
      <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: 'var(--pd-warning)' }} />
      <div className="flex-1 leading-relaxed">
        <div style={{ color: 'var(--pd-warning)', fontWeight: 600 }}>
          Signed in {copy}
        </div>
        <div className="mt-1" style={{ color: 'var(--pd-subtle)' }}>
          {previousLabel ? `Previous session: ${previousLabel}. ` : ''}
          Was that you?
        </div>
        <button
          data-testid={`${testId}-dismiss`}
          onClick={handleDismiss}
          className="mt-2 text-[10px] uppercase tracking-[0.08em] font-semibold underline underline-offset-2 hover:no-underline"
          style={{ color: 'var(--pd-warning)' }}
        >
          Yes, that was me
        </button>
      </div>
      <button
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="p-1 -m-1 rounded-md hover:bg-black/5"
        style={{ color: 'var(--pd-subtle)' }}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
